
import { useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import MainContentWithSidebar from "@/components/MainContentWithSidebar";
import LandingPage from "@/components/LandingPage";
import { useAuth } from "@/contexts/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import { FeedbackForm } from "@/components/FeedbackForm";
import { useIsMobile } from "@/hooks/use-mobile";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import PasswordResetForm from "@/components/auth/PasswordResetForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Expand } from "lucide-react";

type Persona = "travel" | "health" | "meals" | "errands" | "home" | "work";

const Index = () => {
  const { currentUser } = useAuth();
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [showAllMemories, setShowAllMemories] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'reset'>('signup');
  const isMobile = useIsMobile();

  const handlePersonaSelect = (persona: Persona | null) => {
    setSelectedPersona(persona);
    setShowAllMemories(false);
  };

  const handleToggleAllMemories = () => {
    setShowAllMemories(!showAllMemories);
    setSelectedPersona(null);
  };

  const handleReturnToChat = () => {
    setSelectedPersona(null);
    setShowAllMemories(false);
  };

  const handleGetStarted = () => {
    setAuthMode('signup');
    setShowAuthDialog(true);
  };

  const handleSignIn = () => {
    setAuthMode('login');
    setShowAuthDialog(true);
  };

  const handleSwitchToLogin = () => {
    setAuthMode('login');
  };

  const handleSwitchToSignup = () => {
    setAuthMode('signup');
  };

  const handleSwitchToReset = () => {
    setAuthMode('reset');
  };

  if (!currentUser) {
    return (
      <>
        <div className="min-h-screen w-full flex bg-gradient-to-br from-primary/20 via-accent/10 to-secondary overflow-hidden">
          <div className="flex-1 flex items-center justify-center p-4">
            <LandingPage onGetStarted={handleGetStarted} onSignIn={handleSignIn} />
          </div>
        </div>
        
        <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {authMode === 'login' ? 'Welcome Back' : authMode === 'reset' ? 'Reset Password' : 'Get Started'}
              </DialogTitle>
            </DialogHeader>
            {authMode === 'login' ? (
              <LoginForm onSwitchToSignup={handleSwitchToSignup} onSwitchToReset={handleSwitchToReset} />
            ) : authMode === 'reset' ? (
              <PasswordResetForm onSwitchToLogin={handleSwitchToLogin} />
            ) : (
              <SignupForm onSwitchToLogin={handleSwitchToLogin} onSwitchToReset={handleSwitchToReset} />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex bg-gradient-to-br from-primary/20 via-accent/10 to-secondary overflow-hidden">
        <AppSidebar 
          selectedPersona={selectedPersona}
          onPersonaSelect={handlePersonaSelect}
          onShowAllMemories={handleToggleAllMemories}
          showAllMemories={showAllMemories}
          onReturnToChat={handleReturnToChat}
        />
        
        <div className="flex-1 flex flex-col w-full overflow-hidden">
          {/* Header with Sidebar Trigger, User Menu, and Feedback Form */}
          <div className="flex justify-between items-center p-2 sm:p-4 flex-shrink-0">
            <SidebarTrigger className="md:hidden bg-white/20 hover:bg-white/30 text-graphite border border-white/30 h-10 w-32 rounded-lg shadow-lg backdrop-blur-sm flex items-center justify-center gap-2 px-4">
              <Expand className="h-5 w-5" />
              <span className="text-sm font-medium">Menu</span>
            </SidebarTrigger>
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="scale-75 sm:scale-100">
                <FeedbackForm />
              </div>
              <UserMenu />
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 p-2 sm:p-4 pt-0 overflow-hidden">
            <MainContentWithSidebar
              selectedPersona={selectedPersona}
              onPersonaSelect={handlePersonaSelect}
              showAllMemories={showAllMemories}
              onToggleAllMemories={handleToggleAllMemories}
            />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Index;
